/*
 * Carousel Component
 *
 * CSS-first carousel using scroll-snap with JS enhancement.
 * Works without JS as a scrollable container.
 *
 * Keyboard navigation:
 * - Arrow Left/Right: Navigate between slides
 * - Home: First slide
 * - End: Last slide
 *
 * Progressive enhancement: Without JS, carousel is scrollable
 */

/* Wrapped in an IIFE so the class declarations are function-scoped: a page with
   multiple carousel sections includes this asset more than once, and top-level
   `class` declarations would otherwise throw "already declared" on the second
   load. The customElements.define below is still guarded so the element is
   only registered once. */
(function () {
// ============================================
// Carousel Class
// ============================================
class Carousel {
  constructor(container) {
    this.container = container;
    this.viewport = container.querySelector('.carousel-viewport');
    // Find track inside viewport first, fallback to container search
    this.track = this.viewport?.querySelector('.carousel-track')
      || container.querySelector('.carousel-viewport .carousel-track')
      || container.querySelector('.carousel-track');

    if (!this.track) {
      console.warn('Carousel: No .carousel-track found in', container);
      return;
    }

    this.slides = Array.from(this.track.querySelectorAll(':scope > .carousel-slide'));
    // Find nav buttons in viewport only (exclude thumbnail buttons)
    this.prevBtn = this.viewport?.querySelector('.carousel-prev')
      || container.querySelector('.carousel-header .carousel-prev')
      || container.querySelector(':scope > .carousel-prev');
    this.nextBtn = this.viewport?.querySelector('.carousel-next')
      || container.querySelector('.carousel-header .carousel-next')
      || container.querySelector(':scope > .carousel-next');
    this.pagination = container.querySelector('.carousel-pagination');
    this.dots = this.pagination ? Array.from(this.pagination.querySelectorAll('button')) : [];
    this.scrollbar = container.querySelector('.carousel-scrollbar');
    this.scrollbarThumb = this.scrollbar ? this.scrollbar.querySelector('.carousel-scrollbar-thumb') : null;

    this.currentIndex = 0;
    this.isScrolling = false;
    this.scrollTimeout = null;
    this.scrollByPage = container.dataset.scrollBy === 'page';

    this.init();
  }

  init() {
    if (!this.track || this.slides.length === 0) return;

    // Set initial state
    this.updateState();

    // Navigation button handlers
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Dot click handlers
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        if (this.scrollByPage) {
          // For page-scroll, each dot represents a page
          const visibleCount = this.getVisibleCount();
          this.goTo(index * visibleCount);
        } else {
          this.goTo(index);
        }
      });
    });

    // Keyboard navigation on viewport
    if (this.viewport) {
      this.viewport.setAttribute('tabindex', '0');
      this.viewport.addEventListener('keydown', (e) => this.handleKeydown(e));
    }

    // Track scroll to update active state
    this.track.addEventListener('scroll', () => this.handleScroll(), { passive: true });

    // Use IntersectionObserver for precise slide detection
    this.setupIntersectionObserver();

    // Initialize custom scrollbar
    this.updateScrollbar();
    this.setupScrollbarDrag();

    // Setup skeleton loading for images
    this.setupImageLoading();

    // Match the dot count to the number of reachable pages/positions, and
    // recompute on resize (the visible count is responsive).
    this.updateDots();
    this._onResize = () => {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        this.updateDots();
        this.syncIndexFromScroll();
        this.updateState();
      }, 150);
    };
    window.addEventListener('resize', this._onResize);
  }

  setupImageLoading() {
    // Find all slides with skeleton elements
    const slidesWithSkeleton = this.track.querySelectorAll('.carousel-slide:has(.skeleton)');

    slidesWithSkeleton.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;

      // Mark as loaded when image loads
      const markLoaded = () => {
        slide.setAttribute('data-loaded', '');
      };

      // Check if already loaded (cached)
      if (img.complete && img.naturalHeight > 0) {
        markLoaded();
      } else {
        img.addEventListener('load', markLoaded, { once: true });
        // Handle error - still mark as loaded to hide skeleton
        img.addEventListener('error', markLoaded, { once: true });
      }
    });
  }

  setupScrollbarDrag() {
    if (!this.scrollbar || !this.scrollbarThumb) return;

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onMouseDown = (e) => {
      isDragging = true;
      startX = e.clientX;
      startScrollLeft = this.track.scrollLeft;
      this.scrollbarThumb.style.transition = 'none';
      this.scrollbar.classList.add('is-dragging');
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    this._onScrollbarMouseMove = (e) => {
      if (!isDragging) return;

      const scrollbarRect = this.scrollbar.getBoundingClientRect();
      const deltaX = e.clientX - startX;
      const scrollbarWidth = scrollbarRect.width;
      const thumbWidth = this.scrollbarThumb.offsetWidth;
      const maxThumbTravel = scrollbarWidth - thumbWidth;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      // Convert thumb movement to scroll movement
      const scrollDelta = (deltaX / maxThumbTravel) * maxScroll;
      this.track.scrollLeft = startScrollLeft + scrollDelta;
    };

    this._onScrollbarMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      this.scrollbarThumb.style.transition = '';
      this.scrollbar.classList.remove('is-dragging');
      document.body.style.userSelect = '';
    };

    this.scrollbarThumb.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', this._onScrollbarMouseMove);
    document.addEventListener('mouseup', this._onScrollbarMouseUp);

    // Also allow clicking on track to jump
    this.scrollbar.addEventListener('click', (e) => {
      if (e.target === this.scrollbarThumb) return;

      const scrollbarRect = this.scrollbar.getBoundingClientRect();
      const clickX = e.clientX - scrollbarRect.left;
      const scrollbarWidth = scrollbarRect.width;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      // Calculate scroll position based on click location
      const scrollTo = (clickX / scrollbarWidth) * maxScroll;
      this.track.scrollTo({ left: scrollTo, behavior: 'smooth' });
    });
  }

  setupIntersectionObserver() {
    const options = {
      root: this.track,
      threshold: 0.5
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isScrolling) {
          const index = this.slides.indexOf(entry.target);
          if (index !== -1 && index !== this.currentIndex) {
            this.currentIndex = index;
            this.updateState();
          }
        }
      });
    }, options);

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  handleScroll() {
    this.isScrolling = true;
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.isScrolling = false;
    }, 150);

    // Update custom scrollbar position
    this.updateScrollbar();

    // Sync index and update dots reactively during scroll
    this.syncIndexFromScroll();
    this.updateState();
  }

  updateScrollbar() {
    if (!this.scrollbarThumb || !this.scrollbar) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 0) {
      // No scrolling needed, hide scrollbar
      this.scrollbar.style.display = 'none';
      return;
    }

    this.scrollbar.style.display = '';

    // Calculate thumb width as ratio of visible area to total
    const thumbWidthPercent = (clientWidth / scrollWidth) * 100;
    this.scrollbarThumb.style.width = `${thumbWidthPercent}%`;

    // Calculate thumb position
    const scrollPercent = scrollLeft / maxScroll;
    const maxThumbLeft = 100 - thumbWidthPercent;
    const thumbLeft = scrollPercent * maxThumbLeft;
    this.scrollbarThumb.style.left = `${thumbLeft}%`;
  }

  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.prev();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.next();
        break;
      case 'Home':
        e.preventDefault();
        this.goTo(0);
        break;
      case 'End':
        e.preventDefault();
        this.goTo(this.slides.length - 1);
        break;
    }
  }

  prev() {
    this.syncIndexFromScroll();
    const step = this.scrollByPage ? this.getVisibleCount() : 1;
    const newIndex = Math.max(0, this.currentIndex - step);
    this.goTo(newIndex);
  }

  next() {
    this.syncIndexFromScroll();
    const step = this.scrollByPage ? this.getVisibleCount() : 1;
    const newIndex = Math.min(this.slides.length - 1, this.currentIndex + step);
    this.goTo(newIndex);
  }

  getVisibleCount() {
    // The configured items-per-view at the current breakpoint, read from the
    // CSS custom property the section sets. More reliable than measuring the
    // track, and correct when a side banner reduces the slide area (measuring
    // the full track would over-count).
    const ipv = parseInt(getComputedStyle(this.container).getPropertyValue('--carousel-items-per-view'), 10);
    if (ipv && ipv > 0) return ipv;

    // Fallback: measure (e.g. if the property is unset for some reason).
    const trackWidth = this.track.clientWidth;
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 0;
    if (slideWidth === 0) return 1;
    return Math.max(1, Math.floor((trackWidth + gap) / (slideWidth + gap)));
  }

  syncIndexFromScroll() {
    // Current index = the slide whose offset (relative to the first slide) is
    // closest to the current scroll position. Robust to leading insets and
    // varying slide widths.
    if (this.slides.length === 0) return;
    const base = this.slides[0].offsetLeft;
    const pos = this.track.scrollLeft;
    let closest = 0;
    let min = Infinity;
    this.slides.forEach((slide, i) => {
      const dist = Math.abs((slide.offsetLeft - base) - pos);
      if (dist < min) {
        min = dist;
        closest = i;
      }
    });
    this.currentIndex = closest;
  }

  goTo(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.currentIndex = index;

    // Offset-aware: position by the slide's actual offset relative to the
    // first slide, so a leading inset (full-bleed track padding, or a pinned
    // banner) doesn't skew it, and non-uniform slide widths still land right.
    const base = this.slides[0] ? this.slides[0].offsetLeft : 0;
    const left = this.slides[index].offsetLeft - base;

    this.track.scrollTo({
      left: left,
      behavior: 'smooth'
    });

    this.updateState();
  }

  updateState() {
    // Update navigation button states (disable at ends for non-looping).
    // Based on scroll position, not index: with multiple items per view the
    // current index never reaches 0 (leftmost visible) or the last slide.
    if (this.prevBtn || this.nextBtn) {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.prevBtn) {
        this.prevBtn.disabled = this.track.scrollLeft <= 1;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = maxScroll <= 0 || this.track.scrollLeft >= maxScroll - 1;
      }
    }

    // Update dot states
    this.dots.forEach((dot, index) => {
      let isActive;
      if (this.scrollByPage) {
        // For page-scroll, calculate which page we're on
        const visibleCount = this.getVisibleCount();
        const currentPage = Math.floor(this.currentIndex / visibleCount);
        isActive = index === currentPage;
      } else {
        isActive = index === this.currentIndex;
      }
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  updateDots() {
    // Show only as many dots as there are reachable positions: one per page in
    // page-scroll mode (ceil(slides / visible)), otherwise one per single-step
    // position (slides - visible + 1). Extra dots from the markup are hidden.
    if (this.dots.length === 0) return;
    const visible = this.getVisibleCount();
    const pageCount = this.scrollByPage
      ? Math.max(1, Math.ceil(this.slides.length / visible))
      : Math.max(1, this.slides.length - visible + 1);
    this.dots.forEach((dot, index) => {
      dot.style.display = index < pageCount ? '' : 'none';
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    clearTimeout(this.scrollTimeout);
    clearTimeout(this._resizeTimeout);
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    if (this._onScrollbarMouseMove) {
      document.removeEventListener('mousemove', this._onScrollbarMouseMove);
      document.removeEventListener('mouseup', this._onScrollbarMouseUp);
    }
  }
}

// ============================================
// Carousel with Thumbnails (add-on behavior)
// ============================================
// Any carousel can have thumbnails. When present, thumbnails sync with
// the main carousel navigation (scroll-based, not hidden panels).

class CarouselWithThumbnails extends Carousel {
  constructor(container) {
    super(container);

    // Find thumbnails section
    this.thumbnailsContainer = container.querySelector('.carousel-thumbnails');
    if (!this.thumbnailsContainer) return;

    this.thumbnailTrack = this.thumbnailsContainer.querySelector('.carousel-track');
    this.thumbnailSlides = this.thumbnailTrack
      ? Array.from(this.thumbnailTrack.querySelectorAll('.carousel-slide'))
      : [];

    // Thumbnail navigation buttons
    this.thumbnailPrevBtn = this.thumbnailsContainer.querySelector('.carousel-prev');
    this.thumbnailNextBtn = this.thumbnailsContainer.querySelector('.carousel-next');

    // Detect thumbnail position (left/right = vertical, bottom = horizontal)
    this.thumbnailPosition = container.dataset.thumbnailsPosition || 'bottom';
    this.isVerticalThumbnails = this.thumbnailPosition === 'left' || this.thumbnailPosition === 'right';

    this.initThumbnails();
  }

  initThumbnails() {
    if (this.thumbnailSlides.length === 0) return;

    // Add click handlers to thumbnail buttons
    this.thumbnailSlides.forEach((slide, index) => {
      const button = slide.querySelector('button[role="tab"]');
      if (button) {
        button.addEventListener('click', () => this.goTo(index));
      } else {
        // If no button, make the slide itself clickable
        slide.addEventListener('click', () => this.goTo(index));
      }
    });

    // Thumbnail navigation arrow handlers
    if (this.thumbnailPrevBtn) {
      this.thumbnailPrevBtn.addEventListener('click', () => this.scrollThumbnails('prev'));
    }
    if (this.thumbnailNextBtn) {
      this.thumbnailNextBtn.addEventListener('click', () => this.scrollThumbnails('next'));
    }

    // Update thumbnail arrow states on scroll
    if (this.thumbnailTrack) {
      this.thumbnailTrack.addEventListener('scroll', () => this.updateThumbnailArrows(), { passive: true });
    }

    // Set initial thumbnail selection and arrow states
    this.updateThumbnailState();
    this.updateThumbnailArrows();

    // Setup skeleton loading for thumbnail images
    this.setupThumbnailImageLoading();
  }

  setupThumbnailImageLoading() {
    if (!this.thumbnailTrack) return;

    // Find all thumbnail slides with skeleton elements
    const slidesWithSkeleton = this.thumbnailTrack.querySelectorAll('.carousel-slide:has(.skeleton)');

    slidesWithSkeleton.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;

      const markLoaded = () => {
        slide.setAttribute('data-loaded', '');
      };

      // Check if already loaded (cached)
      if (img.complete && img.naturalHeight > 0) {
        markLoaded();
      } else {
        img.addEventListener('load', markLoaded, { once: true });
        img.addEventListener('error', markLoaded, { once: true });
      }
    });
  }

  scrollThumbnails(direction) {
    if (!this.thumbnailTrack) return;

    const scrollAmount = this.isVerticalThumbnails
      ? this.thumbnailTrack.clientHeight * 0.8
      : this.thumbnailTrack.clientWidth * 0.8;

    const currentScroll = this.isVerticalThumbnails
      ? this.thumbnailTrack.scrollTop
      : this.thumbnailTrack.scrollLeft;

    const newScroll = direction === 'prev'
      ? currentScroll - scrollAmount
      : currentScroll + scrollAmount;

    this.thumbnailTrack.scrollTo({
      [this.isVerticalThumbnails ? 'top' : 'left']: Math.max(0, newScroll),
      behavior: 'smooth'
    });
  }

  updateThumbnailArrows() {
    if (!this.thumbnailTrack) return;

    const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = this.thumbnailTrack;

    if (this.isVerticalThumbnails) {
      const atStart = scrollTop <= 0;
      const atEnd = scrollTop >= scrollHeight - clientHeight - 1;

      if (this.thumbnailPrevBtn) this.thumbnailPrevBtn.disabled = atStart;
      if (this.thumbnailNextBtn) this.thumbnailNextBtn.disabled = atEnd;
    } else {
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;

      if (this.thumbnailPrevBtn) this.thumbnailPrevBtn.disabled = atStart;
      if (this.thumbnailNextBtn) this.thumbnailNextBtn.disabled = atEnd;
    }
  }

  updateThumbnailState() {
    if (!this.thumbnailSlides || !this.thumbnailSlides.length) return;

    // Update aria-selected on thumbnails
    this.thumbnailSlides.forEach((slide, i) => {
      const button = slide.querySelector('button[role="tab"]');
      const isSelected = i === this.currentIndex;

      if (button) {
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      }
      slide.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    // Scroll active thumbnail into view (center it)
    const activeThumb = this.thumbnailSlides[this.currentIndex];
    if (activeThumb && this.thumbnailTrack) {
      if (this.isVerticalThumbnails) {
        // Vertical scrolling for left/right positions
        const thumbTop = activeThumb.offsetTop;
        const thumbHeight = activeThumb.offsetHeight;
        const trackHeight = this.thumbnailTrack.clientHeight;

        // Calculate scroll position to center the thumbnail
        const targetScroll = thumbTop - (trackHeight / 2) + (thumbHeight / 2);

        this.thumbnailTrack.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      } else {
        // Horizontal scrolling for bottom position
        const thumbLeft = activeThumb.offsetLeft;
        const thumbWidth = activeThumb.offsetWidth;
        const trackWidth = this.thumbnailTrack.clientWidth;

        // Calculate scroll position to center the thumbnail
        const targetScroll = thumbLeft - (trackWidth / 2) + (thumbWidth / 2);

        this.thumbnailTrack.scrollTo({
          left: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      }
    }
  }

  // Override updateState to also update thumbnails
  updateState() {
    super.updateState();
    this.updateThumbnailState();
  }
}

// Legacy alias for backwards compatibility
const GalleryCarousel = CarouselWithThumbnails;

// ============================================
// Custom element wrapper
// ============================================
// Binds the carousel engine to a <carousel-component class="carousel"> host.
// Uses CarouselWithThumbnails when a .carousel-thumbnails child is present.
class CarouselComponent extends HTMLElement {
  connectedCallback() {
    if (this._instance) return;
    const hasThumbnails = this.querySelector('.carousel-thumbnails');
    this._instance = hasThumbnails
      ? new CarouselWithThumbnails(this)
      : new Carousel(this);
  }

  disconnectedCallback() {
    if (this._instance && typeof this._instance.destroy === 'function') {
      this._instance.destroy();
    }
    this._instance = null;
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}
})();
